// overlayTheme.js
// Event-scoped overlay theming. A "palette" is a partial map of friendly keys
// (gold, pink, grad1, …) to CSS color values. useOverlayPalette resolves the
// active palette (URL ?theme= override → config/stream) and writes the
// corresponding --jop-* custom properties onto document.documentElement, so
// every overlay surface — CSS vars, rgba(var(--jop-*-rgb), a) alphas, sparkle
// fields — retunes live with zero per-component knowledge.
//
// Defaults live in src/index.css :root. An unset key simply keeps the brand
// default, so `palette: {}` (or no theme at all) is pixel-identical to today.

// Friendly palette key → CSS var base name. Keys with rgb:true also emit a
// bare "r,g,b" twin (--jop-foo-rgb) for rgba() alpha composition.
const PALETTE_VARS = {
  blue:            { cssVar: "--jop-blue",              rgb: true },
  highlightBlue:   { cssVar: "--jop-highlight-blue",    rgb: true },
  orange:          { cssVar: "--jop-orange",            rgb: true },
  gold:            { cssVar: "--jop-gold",              rgb: true },
  pink:            { cssVar: "--jop-pink",              rgb: true },
  cream:           { cssVar: "--jop-cream",             rgb: true },
  navy:            { cssVar: "--jop-navy",              rgb: true },
  ink:             { cssVar: "--jop-ink",               rgb: true },
  stage:           { cssVar: "--jop-stage",             rgb: true },
  bgWarm:          { cssVar: "--jop-bg-warm",           rgb: false },
  bgWarmNavy:      { cssVar: "--jop-bg-warm-navy",      rgb: false },
  grad1:           { cssVar: "--jop-grad-1",            rgb: false },
  grad2:           { cssVar: "--jop-grad-2",            rgb: false },
  grad3:           { cssVar: "--jop-grad-3",            rgb: false },
  slotDrawing:     { cssVar: "--jop-slot-drawing",      rgb: true },
  slotDrawingDeep: { cssVar: "--jop-slot-drawing-deep", rgbOnly: true },
  slotCooking:     { cssVar: "--jop-slot-cooking",      rgb: true },
  slotCookingDeep: { cssVar: "--jop-slot-cooking-deep", rgbOnly: true },
  slotDone:        { cssVar: "--jop-slot-done",         rgb: true },
  slotDoneDeep:    { cssVar: "--jop-slot-done-deep",    rgbOnly: true },
};

// Theme presets. `classic` is the empty palette — pure brand defaults.
// Values here are starting points meant to be tuned visually in the
// Overlay Preview Hub (/overlay-previews, append ?theme=<id> to a scene URL).
export const OVERLAY_THEMES = {
  classic: {
    id: "classic",
    label: "Classic JoP",
    palette: {},
  },
  zoofari: {
    id: "zoofari",
    label: "Zoofari — Pink Flamingo",
    // Pink IS the field: flamingo-rose column/bar gradients over dark plum
    // backgrounds. Text carries the variety — warm gold headers, cream body,
    // classic sky blue stats. Sage survives only as small pattern tints
    // (v1 all-sage field read as military — Dan 2026-06-09).
    palette: {
      grad1: "#e8729e",       // column/bar gradient: flamingo → deep rose
      grad2: "#c14e79",
      grad3: "#8e3258",
      ink: "#1a0d14",         // plum-black panel base
      stage: "#2b1622",       // dark rosewood backdrop (Outro, FaceCam pills)
      bgWarm: "#170d12",
      bgWarmNavy: "#1c1118",  // StartingSoon stage base
      gold: "#ffce63",        // emphasis stays warm gold — pops on pink, ties to brand
      pink: "#ff5c95",        // hot flamingo pops
      orange: "#ff8b6b",      // coral accents
      cream: "#fff1e6",       // warm-white body text
      blue: "#9caf88",        // muted sage — gingham threads + rare tints only
      navy: "#5c2742",        // deep plum badge/QR fills
      // highlightBlue intentionally unset → classic #4aa4e1 stays.
      // slot colors intentionally unset → guests' learned status colors stay.
    },
  },
};

export function hexToRgbTriplet(hex) {
  const m = /^#?([0-9a-f]{6})$/i.exec(String(hex).trim());
  if (!m) return null;
  const n = parseInt(m[1], 16);
  return `${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}`;
}

// Resolve a partial palette object into a { "--jop-*": value } map.
export function paletteToCssVars(palette) {
  const vars = {};
  if (!palette) return vars;
  for (const [key, value] of Object.entries(palette)) {
    const def = PALETTE_VARS[key];
    if (!def || !value) continue;
    const triplet = hexToRgbTriplet(value);
    if (def.rgbOnly) {
      // Deep gradient partners only exist as rgb triplets.
      if (triplet) vars[`${def.cssVar}-rgb`] = triplet;
      continue;
    }
    vars[def.cssVar] = value;
    if (def.rgb && triplet) vars[`${def.cssVar}-rgb`] = triplet;
  }
  return vars;
}

// Resolve the active palette from stream config + optional URL override.
// Order: ?theme=<presetId> in the overlay URL (preview without touching live
// config) → config.overlayTheme preset → per-key custom overrides on top.
export function resolveOverlayPalette(config, search) {
  const params = new URLSearchParams(search ?? window.location.search);
  const urlTheme = params.get("theme");
  if (urlTheme && OVERLAY_THEMES[urlTheme]) {
    return OVERLAY_THEMES[urlTheme].palette;
  }
  const preset = OVERLAY_THEMES[config?.overlayTheme]?.palette || {};
  const custom = config?.overlayPalette || {};
  return { ...preset, ...custom };
}
